
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from '@/components/ui/drawer';
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription } from '@/components/ui/form';
import { DevelopmentStep } from '@/types/develop';
import { cn } from '@/lib/utils';
import { Trash2, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from '@/components/ui/use-toast';
import { Textarea } from '@/components/ui/textarea';

interface MobileStepEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step: DevelopmentStep | null;
  onSave: (step: DevelopmentStep) => void;
  onDelete?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isNew?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  darkMode?: boolean;
}

export const MobileStepEditor: React.FC<MobileStepEditorProps> = ({
  open,
  onOpenChange,
  step,
  onSave,
  onDelete,
  onMoveUp,
  onMoveDown,
  isNew = false,
  isFirst = false,
  isLast = false,
  darkMode = false,
}) => {
  const [errors, setErrors] = useState<{
    name?: string;
    time?: string;
    agitationInterval?: string;
    agitationDuration?: string;
    notes?: string;
  }>({});
  
  // Character limits
  const NAME_MAX_LENGTH = 20;
  const TIME_MAX_DIGITS = 4;
  const AGITATION_MAX_DIGITS = 3;
  const NOTES_MAX_LENGTH = 50;
  
  const form = useForm({
    defaultValues: step || {
      id: '',
      name: '',
      time: 60,
      agitationType: 'intermittent',
      agitationInterval: 30,
      agitationDuration: 10,
      notes: '',
    },
  });

  // Reset form when step changes
  useEffect(() => {
    if (step) {
      form.reset(step);
    }
  }, [step, form]);

  // Validate name input
  const validateNameInput = (value: string): boolean => {
    if (!value.trim()) {
      setErrors(prev => ({ ...prev, name: 'Step name is required' }));
      return false;
    }
    
    if (value.length > NAME_MAX_LENGTH) {
      setErrors(prev => ({ ...prev, name: `Step name must be ${NAME_MAX_LENGTH} characters or less` }));
      return false;
    }
    
    if (/[<>{}[\]\\\/]/.test(value)) {
      setErrors(prev => ({ ...prev, name: 'Step name contains invalid characters' }));
      return false;
    }
    
    setErrors(prev => ({ ...prev, name: undefined }));
    return true;
  };

  // Validate number input limits
  const validateNumberInput = (
    value: string, 
    fieldName: string, 
    maxDigits: number,
    maxValue: number,
    minValue: number = 1
  ): boolean => {
    const numValue = Number(value);
    
    if (isNaN(numValue)) {
      setErrors(prev => ({ ...prev, [fieldName]: `Must be a number` }));
      return false;
    }
    
    if (numValue < minValue) {
      setErrors(prev => ({ ...prev, [fieldName]: `Must be at least ${minValue}` }));
      return false;
    }
    
    if (numValue > maxValue) {
      setErrors(prev => ({ ...prev, [fieldName]: `Maximum value is ${maxValue}` }));
      return false;
    }
    
    // Clear error if valid
    setErrors(prev => ({ ...prev, [fieldName]: undefined }));
    return true;
  };
  
  // Validate notes input
  const validateNotesInput = (value: string): boolean => {
    if (!value) {
      setErrors(prev => ({ ...prev, notes: undefined }));
      return true;
    }
    
    if (value.length > NOTES_MAX_LENGTH) {
      setErrors(prev => ({ ...prev, notes: `Notes must be ${NOTES_MAX_LENGTH} characters or less` }));
      return false;
    }
    
    // Check for potential security issues
    const maliciousPatterns = [
      /<script/i,
      /javascript:/i,
      /onclick/i,
      /onerror/i,
      /eval\(/i
    ];
    
    for (const pattern of maliciousPatterns) {
      if (pattern.test(value)) {
        setErrors(prev => ({ ...prev, notes: 'Notes contain potentially unsafe content' }));
        return false;
      }
    }
    
    setErrors(prev => ({ ...prev, notes: undefined }));
    return true;
  };

  // Handle input change with max value restrictions
  const handleNumberInput = (e: React.ChangeEvent<HTMLInputElement>, maxDigits: number, maxValue: number) => {
    let value = e.target.value;
    
    // Remove any non-digit characters
    value = value.replace(/\D/g, '');
    
    // Restrict to maximum number of digits
    if (value.length > maxDigits) {
      value = value.slice(0, maxDigits);
    }
    
    // Ensure it doesn't exceed maximum value
    const numValue = Number(value);
    if (numValue > maxValue) {
      value = maxValue.toString();
    }
    
    // Update the input value
    e.target.value = value;
    
    return value;
  };

  const handleSave = () => {
    const values = form.getValues();
    
    // Validate all fields
    const isNameValid = validateNameInput(values.name);
    const isTimeValid = validateNumberInput(String(values.time), 'time', TIME_MAX_DIGITS, 9999);
    
    // Validate agitation values if needed
    let isAgitationIntervalValid = true;
    let isAgitationDurationValid = true;
    
    if (values.agitationType === 'intermittent') {
      isAgitationIntervalValid = validateNumberInput(
        String(values.agitationInterval), 
        'agitationInterval', 
        AGITATION_MAX_DIGITS, 
        999
      );
    }
    
    if (values.agitationType !== 'none') {
      isAgitationDurationValid = validateNumberInput(
        String(values.agitationDuration), 
        'agitationDuration', 
        AGITATION_MAX_DIGITS, 
        999
      );
    }
    
    const isNotesValid = validateNotesInput(values.notes || '');
    
    // Only save if all validations pass
    if (isNameValid && isTimeValid && 
        isAgitationIntervalValid && isAgitationDurationValid && isNotesValid) {
      onSave({
        ...values,
        time: Number(values.time),
        agitationInterval: values.agitationType === 'intermittent' ? Number(values.agitationInterval) : undefined,
        agitationDuration: values.agitationType !== 'none' ? Number(values.agitationDuration) : undefined,
      });
      onOpenChange(false);
      toast({
        title: isNew ? "Step created" : "Step updated",
        description: `Successfully ${isNew ? 'added' : 'updated'} ${values.name}`,
      });
    }
  };

  // Check if form has any validation errors
  const hasErrors = Object.values(errors).some(error => error !== undefined);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className={cn(
        darkMode && 'bg-black border-t border-red-900'
      )}>
        <DrawerHeader>
          <DrawerTitle className={cn(
            darkMode && 'text-red-400'
          )}>
            {isNew ? 'Add Step' : 'Edit Step'}
          </DrawerTitle>
        </DrawerHeader>
        
        <div className="px-4 pb-2">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" className={cn(
                darkMode && 'text-red-500'
              )}>
                Step Name*
              </Label>
              <Input
                id="name"
                placeholder="e.g., Developer"
                className={cn(
                  darkMode && 'bg-black border-red-900 text-red-500 placeholder:text-red-800',
                  errors.name && 'border-destructive'
                )}
                {...form.register('name')}
                onChange={(e) => {
                  form.setValue('name', e.target.value);
                  validateNameInput(e.target.value);
                }}
                onBlur={(e) => validateNameInput(e.target.value)}
                maxLength={NAME_MAX_LENGTH}
              />
              {errors.name && (
                <p className="text-xs text-destructive mt-1">{errors.name}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {(form.watch('name')?.length || 0)}/{NAME_MAX_LENGTH}
              </p>
            </div>
            
            <div>
              <Label htmlFor="time" className={cn(
                darkMode && 'text-red-500'
              )}>
                Time (seconds)*
              </Label>
              <Input
                id="time"
                type="number"
                min={1}
                max={9999}
                className={cn(
                  darkMode && 'bg-black border-red-900 text-red-500',
                  errors.time && 'border-destructive'
                )}
                {...form.register('time')}
                onChange={(e) => {
                  const sanitizedValue = handleNumberInput(e, TIME_MAX_DIGITS, 9999);
                  form.setValue('time', sanitizedValue ? Number(sanitizedValue) : 1);
                  validateNumberInput(sanitizedValue || '1', 'time', TIME_MAX_DIGITS, 9999);
                }}
                onBlur={(e) => validateNumberInput(e.target.value, 'time', TIME_MAX_DIGITS, 9999)}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {errors.time && (
                <p className="text-xs text-destructive mt-1">{errors.time}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="agitationType" className={cn(
                darkMode && 'text-red-500'
              )}>
                Agitation Type*
              </Label>
              <Select
                value={form.watch('agitationType')}
                onValueChange={(value) => form.setValue('agitationType', value as 'continuous' | 'intermittent' | 'none')}
              >
                <SelectTrigger className={cn(
                  darkMode && 'bg-black border-red-900 text-red-500'
                )}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(
                  darkMode && 'bg-black border-red-900 text-red-500'
                )}>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="continuous">Continuous</SelectItem>
                  <SelectItem value="intermittent">Intermittent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {form.watch('agitationType') === 'intermittent' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="agitationInterval" className={cn(
                    darkMode && 'text-red-500'
                  )}>
                    Interval (seconds)*
                  </Label>
                  <Input
                    id="agitationInterval"
                    type="number"
                    min={1}
                    max={999}
                    className={cn(
                      darkMode && 'bg-black border-red-900 text-red-500',
                      errors.agitationInterval && 'border-destructive'
                    )}
                    {...form.register('agitationInterval')}
                    onChange={(e) => {
                      const sanitizedValue = handleNumberInput(e, AGITATION_MAX_DIGITS, 999);
                      form.setValue('agitationInterval', sanitizedValue ? Number(sanitizedValue) : 1);
                      validateNumberInput(sanitizedValue || '1', 'agitationInterval', AGITATION_MAX_DIGITS, 999);
                    }}
                    onBlur={(e) => validateNumberInput(e.target.value, 'agitationInterval', AGITATION_MAX_DIGITS, 999)}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  {errors.agitationInterval && (
                    <p className="text-xs text-destructive mt-1">{errors.agitationInterval}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="agitationDuration" className={cn(
                    darkMode && 'text-red-500'
                  )}>
                    Duration (seconds)*
                  </Label>
                  <Input
                    id="agitationDuration"
                    type="number"
                    min={1}
                    max={999}
                    className={cn(
                      darkMode && 'bg-black border-red-900 text-red-500',
                      errors.agitationDuration && 'border-destructive'
                    )}
                    {...form.register('agitationDuration')}
                    onChange={(e) => {
                      const sanitizedValue = handleNumberInput(e, AGITATION_MAX_DIGITS, 999);
                      form.setValue('agitationDuration', sanitizedValue ? Number(sanitizedValue) : 1);
                      validateNumberInput(sanitizedValue || '1', 'agitationDuration', AGITATION_MAX_DIGITS, 999);
                    }}
                    onBlur={(e) => validateNumberInput(e.target.value, 'agitationDuration', AGITATION_MAX_DIGITS, 999)}
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                  {errors.agitationDuration && (
                    <p className="text-xs text-destructive mt-1">{errors.agitationDuration}</p>
                  )}
                </div>
              </div>
            )}
            
            {form.watch('agitationType') === 'continuous' && (
              <div>
                <Label htmlFor="agitationDuration" className={cn(
                  darkMode && 'text-red-500'
                )}>
                  Duration (seconds)*
                </Label>
                <Input
                  id="agitationDuration"
                  type="number"
                  min={1}
                  max={999}
                  className={cn(
                    darkMode && 'bg-black border-red-900 text-red-500',
                    errors.agitationDuration && 'border-destructive'
                  )}
                  {...form.register('agitationDuration')}
                  onChange={(e) => {
                    const sanitizedValue = handleNumberInput(e, AGITATION_MAX_DIGITS, 999);
                    form.setValue('agitationDuration', sanitizedValue ? Number(sanitizedValue) : 1);
                    validateNumberInput(sanitizedValue || '1', 'agitationDuration', AGITATION_MAX_DIGITS, 999);
                  }}
                  onBlur={(e) => validateNumberInput(e.target.value, 'agitationDuration', AGITATION_MAX_DIGITS, 999)}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {errors.agitationDuration && (
                  <p className="text-xs text-destructive mt-1">{errors.agitationDuration}</p>
                )}
              </div>
            )}
            
            <div>
              <Label htmlFor="notes" className={cn(
                darkMode && 'text-red-500'
              )}>
                Notes (optional)
              </Label>
              <Textarea
                id="notes"
                placeholder="Any special instructions or notes"
                className={cn(
                  darkMode && 'bg-black border-red-900 text-red-500 placeholder:text-red-800',
                  errors.notes && 'border-destructive'
                )}
                maxLength={NOTES_MAX_LENGTH}
                {...form.register('notes')}
                onChange={(e) => {
                  form.setValue('notes', e.target.value);
                  validateNotesInput(e.target.value);
                }}
                onBlur={(e) => validateNotesInput(e.target.value)}
              />
              {errors.notes && (
                <p className="text-xs text-destructive mt-1">{errors.notes}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                {(form.watch('notes')?.length || 0)}/{NOTES_MAX_LENGTH}
              </p>
            </div>
            
            {!isNew && (
              <div className="flex justify-between items-center pt-2">
                <Label className={cn(
                  darkMode && 'text-red-500'
                )}>
                  Change Step Order
                </Label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={isFirst}
                    onClick={onMoveUp}
                    className={cn(
                      darkMode && 'border-red-900 text-red-500 hover:bg-red-950',
                      'flex items-center gap-1'
                    )}
                  >
                    <ArrowUp className="h-4 w-4" />
                    Move Up
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={isLast}
                    onClick={onMoveDown}
                    className={cn(
                      darkMode && 'border-red-900 text-red-500 hover:bg-red-950',
                      'flex items-center gap-1'
                    )}
                  >
                    <ArrowDown className="h-4 w-4" />
                    Move Down
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <DrawerFooter className="flex-row space-x-2">
          {!isNew && onDelete && (
            <Button 
              variant="destructive" 
              onClick={onDelete}
              className={cn(
                'flex-1',
                darkMode && 'bg-red-900 hover:bg-red-800 text-white'
              )}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
          
          <Button 
            onClick={handleSave}
            disabled={hasErrors}
            className={cn(
              'flex-1',
              darkMode && 'bg-red-800 hover:bg-red-700 text-white',
              hasErrors && 'opacity-50 cursor-not-allowed'
            )}
          >
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
